import React, { useRef, useEffect, useState } from 'react';

export interface ReelPlayerProps {
  /** SAS or direct video URL – stream in <video src>, do not fetch as blob. */
  videoUrl: string;
  /** Called when reel becomes visible (for seenIds). */
  onVisible?: () => void;
  /** Optional className for the wrapper. */
  className?: string;
  /** When true, play; when false, pause. If set, overrides IntersectionObserver for play/pause. */
  isActive?: boolean;
  /**
   * When true, render the native video without the `muted` attribute so audio can play.
   * Only set this from a user-initiated context (e.g. a modal opened on click) to comply
   * with browser autoplay policies. Defaults to muted (feed behaviour).
   */
  playWithAudio?: boolean;
}

type MediaState = 'idle' | 'loading' | 'ready' | 'error';

function isValidVideoUrl(url: string): boolean {
  const trimmed = url.trim();
  return trimmed.length > 0 && (trimmed.startsWith('http://') || trimmed.startsWith('https://') || trimmed.startsWith('blob:'));
}

/**
 * Native <video> that autoplays when visible (or when isActive) and pauses when not.
 * Streams directly from videoUrl; no blob/base64 fetch.
 * Shows loading/error overlays instead of an empty black frame.
 */
const ReelPlayer: React.FC<ReelPlayerProps> = ({
  videoUrl,
  onVisible,
  className = '',
  isActive,
  playWithAudio = false,
}) => {
  const videoRef = useRef<HTMLVideoElement>(null);
  const hasReportedVisible = useRef(false);
  const [mediaState, setMediaState] = useState<MediaState>(() =>
    isValidVideoUrl(videoUrl) ? 'loading' : 'error'
  );

  useEffect(() => {
    hasReportedVisible.current = false;
    setMediaState(isValidVideoUrl(videoUrl) ? 'loading' : 'error');
  }, [videoUrl]);

  useEffect(() => {
    const video = videoRef.current;
    if (!video || !isValidVideoUrl(videoUrl)) return;

    const handleLoaded = () => setMediaState('ready');
    const handleError = () => setMediaState('error');

    video.addEventListener('loadedmetadata', handleLoaded);
    video.addEventListener('canplay', handleLoaded);
    video.addEventListener('error', handleError);

    if (typeof isActive === 'boolean') {
      if (isActive) {
        video.play().catch(() => {});
        if (onVisible && !hasReportedVisible.current) {
          hasReportedVisible.current = true;
          onVisible();
        }
      } else {
        video.pause();
      }
      return () => {
        video.removeEventListener('loadedmetadata', handleLoaded);
        video.removeEventListener('canplay', handleLoaded);
        video.removeEventListener('error', handleError);
      };
    }

    const observer = new IntersectionObserver(
      (entries) => {
        const [entry] = entries;
        if (!entry) return;
        const isVisible = entry.isIntersecting;
        if (isVisible) {
          video.play().catch(() => {});
          if (onVisible && !hasReportedVisible.current) {
            hasReportedVisible.current = true;
            onVisible();
          }
        } else {
          video.pause();
        }
      },
      { threshold: 0.5, rootMargin: '0px' }
    );

    observer.observe(video);
    return () => {
      observer.disconnect();
      video.removeEventListener('loadedmetadata', handleLoaded);
      video.removeEventListener('canplay', handleLoaded);
      video.removeEventListener('error', handleError);
    };
  }, [videoUrl, onVisible, isActive]);

  const showVideo = isValidVideoUrl(videoUrl) && mediaState !== 'error';

  return (
    <div
      className={className}
      style={{
        position: 'relative',
        width: '100%',
        height: '100%',
        background: '#111',
      }}
    >
      {showVideo && (
        <video
          ref={videoRef}
          src={videoUrl}
          playsInline
          controls={false}
          muted={!playWithAudio}
          loop
          preload="metadata"
          style={{
            width: '100%',
            height: '100%',
            objectFit: 'cover',
            display: mediaState === 'ready' ? 'block' : 'none',
          }}
        />
      )}

      {mediaState === 'loading' && (
        <div
          className="d-flex flex-column align-items-center justify-content-center text-white-50"
          style={{ position: 'absolute', inset: 0 }}
          role="status"
          aria-live="polite"
        >
          <span className="spinner-border spinner-border-sm mb-2" aria-hidden="true" />
          <span className="small">Video yükleniyor…</span>
        </div>
      )}

      {mediaState === 'error' && (
        <div
          className="d-flex flex-column align-items-center justify-content-center text-white-50 p-3 text-center"
          style={{ position: 'absolute', inset: 0 }}
          role="alert"
        >
          <i className="fas fa-exclamation-triangle fa-2x mb-2 text-warning" aria-hidden="true" />
          <span className="small">Video yüklenemedi</span>
        </div>
      )}
    </div>
  );
};

export default ReelPlayer;
